import { createContext, FC, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBrandingService } from '../apis/branding/useBrandingService';
import { localStorageGet } from '../utils/localStorage';

type TBrandingContextValue = {
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  faviconUrl?: string;
  brandingLoaded: boolean;
};

const BrandingContext = createContext<TBrandingContextValue>({
  brandingLoaded: false,
});

/** Decode institute_ref from the stored JWT without a library dependency. */
const getInstituteIdFromToken = (): string | undefined => {
  try {
    const token = localStorageGet('token', '') as string;
    if (!token) return undefined;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.institute_ref ?? payload?.instituteId ?? undefined;
  } catch {
    return undefined;
  }
};

export const BrandingProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { getBranding } = useBrandingService();
  const instituteId = getInstituteIdFromToken();

  const { data: branding, isSuccess } = useQuery({
    queryKey: ['branding', instituteId],
    queryFn: () => getBranding(instituteId),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });

  const faviconAsset = branding?.assets?.find((a) => a.type === 'favicon');
  const faviconUrl = faviconAsset?.fileUrl;

  useEffect(() => {
    if (!faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [faviconUrl]);

  const value = useMemo<TBrandingContextValue>(
    () => ({
      logoUrl: branding?.logoUrl,
      primaryColor: branding?.primaryColor,
      secondaryColor: branding?.secondaryColor,
      accentColor: branding?.accentColor,
      faviconUrl,
      brandingLoaded: isSuccess,
    }),
    [branding, faviconUrl, isSuccess]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBrandingContext = () => useContext(BrandingContext);
